// Modal breakdown for the "التزام شهري / Monthly Recurring" counter card.
// Shows both fixed monthly commitments AND active installments in one glance.
import { X, Repeat, Car, Coins } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useCommitments } from "@/lib/useCommitmentsCache";
import { useInstallments } from "@/lib/useInstallmentsCache";

function fmt(n, ccy) {
  return `${Math.round(Number(n || 0)).toLocaleString()} ${ccy}`;
}

export function RecurringDetails({ open, onClose, ccy }) {
  const { t, lang } = useI18n();
  const commits = useCommitments();
  const inst = useInstallments();
  if (!open) return null;

  const active = (inst.active || []).filter((i) => (i.total_installments - (i.paid_count || 0)) > 0);
  const commitList = (commits.items || []).filter((c) => c.active !== false);
  const commitTotal = commits.total_monthly || 0;
  const instTotal = inst.total_monthly || 0;
  const grand = commitTotal + instTotal;

  return (
    <div data-testid="recurring-details-modal" className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm grid place-items-end sm:place-items-center p-3" onClick={onClose}>
      <div className="w-full sm:max-w-md neu p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-extrabold neu-accent-blue flex items-center gap-2">
            <Repeat className="w-4 h-4" />{lang === "ar" ? "تفاصيل التزاماتي الشهرية" : "Monthly recurring details"}
          </h3>
          <button onClick={onClose} data-testid="recurring-details-close" className="w-9 h-9 rounded-full neu-sm grid place-items-center active:scale-95">
            <X className="w-4 h-4" />
          </button>
        </div>

        <section className="space-y-2 mb-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">🟢 {t("fixedCommitments")}</p>
          {commitList.length === 0 && <p className="text-xs text-slate-400 py-1">—</p>}
          {commitList.map((c) => (
            <div key={c.id} data-testid={`recur-commit-${c.id}`} className="p-2.5 rounded-xl neu-sm flex items-center gap-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{c.name}</p>
                <p className="text-xs text-slate-500">{Number(c.amount).toFixed(0)} {c.currency}/{t("month")}</p>
              </div>
              <span className="font-display font-extrabold text-sm tabular-nums text-emerald-600">{Number(c.amount).toFixed(0)}</span>
            </div>
          ))}
        </section>

        <section className="space-y-2 mb-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Car className="w-3.5 h-3.5" />{t("installments")}</p>
          {active.length === 0 && <p className="text-xs text-slate-400 py-1">—</p>}
          {active.map((i) => (
            <div key={i.id} data-testid={`recur-inst-${i.id}`} className="p-2.5 rounded-xl neu-sm flex items-center gap-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{i.name}</p>
                <p className="text-xs text-slate-500">{(i.amount || 0).toFixed(0)} {ccy}/{t("month")}</p>
              </div>
              <span className="font-display font-extrabold text-sm tabular-nums text-indigo-600">{(i.amount || 0).toFixed(0)}</span>
            </div>
          ))}
        </section>

        <div className="p-3 rounded-2xl neu-inset flex items-center justify-between">
          <span className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
            <Coins className="w-3.5 h-3.5" />{lang === "ar" ? "إجمالي شهري" : "Monthly total"}
          </span>
          <span data-testid="recur-total" className="font-display text-xl font-extrabold neu-accent-blue tabular-nums">{fmt(grand, ccy)}</span>
        </div>
      </div>
    </div>
  );
}
